import { useState, useEffect, useRef, Fragment } from 'react';
import classNames from 'classnames';
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'components/shared/image';
import useIsMobile from 'hooks/utils/useIsMobile';
import styles from './productGallery.module.scss';
import VideoPlayer from '../../../shared/video/video';
import Play from '../../../shared/play';

const ProductGallery = ({ thumbnail, productPictures, productVideos }) => {
  const [activeImage, setActiveImage] = useState(0);
  const activeImageRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderRef, slider] = useKeenSlider({
    initial: 0,
    slidesPerView: 1,
    slideChanged(s) {
      setCurrentSlide(s.track?.details?.abs);
    },
  });

  const isMobile = useIsMobile();

  useEffect(() => {
    // when switching from mobile to desktop and back
    setCurrentSlide(0);
  }, [isMobile]);

  // This effect will only be called on mobile devices
  useEffect(() => {
    activeImageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeImage]);

  return (
    <div className={styles.productGallery}>
      <div className={styles.productPictures}>
        <div className={styles.productGalleryRow}>
          {!isMobile &&
            productPictures.map((productPicture, index) => (
              <div
                key={`desktop-${productPicture.uid}`}
                role="button"
                tabIndex={0}
                className={classNames(styles.productGalleryPicture, {
                  [styles.activeGalleryPicture]: activeImage === index,
                })}
                onClick={() => setActiveImage(index)}
                onKeyDown={() => setActiveImage(index)}
              >
                <Image src={productPicture} loading="eager" layout="fill" sizes="10vw" priority />
              </div>
            ))}
          {!isMobile &&
            productVideos?.map(({ video, thumbnail: videoThumbnail }, index) => (
              <div
                key={`video-thumbnail-${video?.title}`}
                role="button"
                tabIndex={0}
                className={classNames(styles.productGalleryPicture, {
                  [styles.activeGalleryPicture]: activeImage === productPictures?.length + index,
                })}
                onClick={() => setActiveImage(productPictures?.length + index)}
                onKeyDown={() => setActiveImage(productPictures?.length + index)}
              >
                <Image src={videoThumbnail} loading="eager" layout="fill" sizes="10vw" priority />
                <Play width="2rem" height="2rem" />
              </div>
            ))}
          {isMobile && (
            <div>
              {[
                ...Array(
                  slider?.current?.slides?.length || productPictures?.length + productVideos?.length
                )?.keys(),
              ]?.map(idx => (
                <button
                  key={idx}
                  type="button"
                  tabIndex={0}
                  aria-label="Switch product picture"
                  className={classNames(
                    styles.productGallerySphere,
                    {
                      selectedSphere: currentSlide === idx,
                    },
                    currentSlide === idx ? styles.selectedSphere : ''
                  )}
                  onClick={() => slider.current.moveToIdx(idx)}
                />
              ))}
            </div>
          )}
        </div>
        {isMobile ? (
          // Mobile images in a Slider
          <div className={classNames('keen-slider')} ref={sliderRef}>
            {productPictures.map((productPicture, index) => (
              <Fragment key={`mobile-${productPicture.uid}`}>
                <Image
                  ref={activeImage === index ? activeImageRef : null}
                  className={classNames(styles.productImageSlider, 'keen-slider__slide')}
                  src={productPicture}
                  loading="eager"
                  layout="fill"
                  priority
                />
              </Fragment>
            ))}
            {productVideos?.map(
              ({ video, thumbnail: videoThumbnail, videoAspectRatio }) =>
                video && (
                  <VideoPlayer
                    key={`mobile-${video.title}`}
                    className={classNames('keen-slider__slide', styles.productVideoSlider)}
                    thumbnail={videoThumbnail}
                    source={video}
                    aspectRatio={videoAspectRatio || 16 / 9}
                  />
                )
            )}
          </div>
        ) : (
          // Desktop images
          <>
            {productPictures?.[activeImage] && (
              <Image
                className={styles.activeProductImage}
                src={productPictures[activeImage] || thumbnail}
                loading="eager"
                layout="fill"
                sizes="50vw"
                priority
              />
            )}
            {productVideos?.map(
              ({ video, thumbnail: videoThumbnail, videoAspectRatio }, index) =>
                video && (
                  <VideoPlayer
                    key={`desktop-${video.title}`}
                    className={classNames(styles.activeProductVideo, {
                      [styles.displayVideo]: index === activeImage - productPictures.length,
                    })}
                    thumbnail={videoThumbnail}
                    source={video}
                    aspectRatio={videoAspectRatio || 16 / 9}
                  />
                )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductGallery;
